'use client';

import { useState } from 'react';
import { Loader2, RefreshCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useResetProductSyncCounter } from '@/lib/api/account-manager';

interface ProductSyncTabProps {
  accountId: string | undefined;
}

export function ProductSyncTab({ accountId }: ProductSyncTabProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const reset = useResetProductSyncCounter(() => setOpen(false));

  const handleConfirm = () => {
    if (!accountId) return;
    reset.mutate({ accountId });
  };

  if (!accountId) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('accountManager.productSync.loadingAccount')}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCcw className="h-5 w-5" />
          {t('accountManager.productSync.title')}
        </CardTitle>
        <CardDescription className="max-w-2xl">
          {t('accountManager.productSync.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
          <p>{t('accountManager.productSync.explainer')}</p>
        </div>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={reset.isPending}>
              {reset.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('accountManager.productSync.resetting')}
                </>
              ) : (
                <>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  {t('accountManager.productSync.resetButton')}
                </>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('accountManager.productSync.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('accountManager.productSync.confirmDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={reset.isPending}>
                {t('accountManager.productSync.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  handleConfirm();
                }}
                disabled={reset.isPending}
              >
                {t('accountManager.productSync.confirmButton')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
